/**
 * HTTP client with automatic Supabase Auth Bearer token injection.
 *
 * All API calls go through this wrapper so auth headers are consistent.
 */

import { supabase } from "../lib/supabase";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

async function getAuthToken(): Promise<string | null> {
  // E2E performance-profiling bypass. Backend only honors this token in
  // non-prod when THREAD_E2E_BYPASS_KEY is set and matches.
  if (typeof window !== "undefined") {
    const e2e = window.localStorage.getItem("thread_e2e_token");
    if (e2e) return e2e;
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function apiClient<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (API_BASE.startsWith("/api") || API_BASE.includes("localhost")) {
    headers["X-User-Id"] = "local-dev";
    headers["X-User-Name"] = "Local Developer";
  }

  // Forward Flow360 tokens in separate custom headers (never overwrite Supabase Authorization)
  const flow360Token = sessionStorage.getItem("flow360_access_token");
  if (flow360Token && path.startsWith("/flow360")) {
    headers["X-Flow360-Authorization"] = `Bearer ${flow360Token}`;
    const flow360User = sessionStorage.getItem("flow360_user_email");
    if (flow360User) {
      headers["X-Flow360-Access-User"] = flow360User;
    }
    const flow360Refresh = sessionStorage.getItem("flow360_refresh_token");
    if (flow360Refresh) {
      headers["X-Flow360-Refresh-Token"] = flow360Refresh;
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "Unknown error");
    throw new Error(`API ${res.status}: ${text}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}
